/**
 * CollectionGroupsPage - Public listing of collection groups
 * Elvis Aloha Collection Archive
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Package, Layers, Archive, BookOpen, Image } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Group type display config
const GROUP_TYPE_CONFIG = {
  curated: { label: 'Curated Collection', icon: Layers, color: 'text-amber-700' },
  exhibition: { label: 'Exhibition', icon: BookOpen, color: 'text-blue-700' },
  category: { label: 'Category', icon: Package, color: 'text-stone-600' },
  research: { label: 'Research', icon: Archive, color: 'text-emerald-700' },
  acquisition: { label: 'Acquisition', icon: Archive, color: 'text-stone-500' },
};

function CollectionGroupCard({ group }) {
  const typeConfig = GROUP_TYPE_CONFIG[group.group_type] || GROUP_TYPE_CONFIG.curated;
  const TypeIcon = typeConfig.icon;
  
  return (
    <Link
      to={`/collection/groups/${group.slug}`}
      className="group block bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-amber-300"
      data-testid={`collection-group-${group.slug}`}
    >
      {/* Hero Image or Placeholder */}
      <div className="aspect-[16/9] bg-gradient-to-br from-stone-100 to-stone-200 relative overflow-hidden">
        {group.hero_image_url ? (
          <img
            src={group.hero_image_url}
            alt={group.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : group.primary_artifact_thumbnail ? (
          <img
            src={group.primary_artifact_thumbnail}
            alt={group.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-16 h-16 text-stone-300" />
          </div>
        )}
        
        {/* Featured Badge */}
        {group.is_featured && (
          <div className="absolute top-3 left-3 bg-amber-600 text-white text-xs font-medium px-2 py-1 rounded-full">
            Featured
          </div>
        )}
        
        {/* Artifact Count */}
        <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
          {group.artifact_count} {group.artifact_count === 1 ? 'item' : 'items'}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5">
        <div className="flex items-center gap-2 text-xs mb-2">
          <TypeIcon className={`w-3.5 h-3.5 ${typeConfig.color}`} />
          <span className={`${typeConfig.color} font-medium`}>{typeConfig.label}</span>
        </div>
        
        <h3 className="text-lg font-semibold text-stone-900 group-hover:text-amber-700 transition-colors line-clamp-2 mb-2">
          {group.name}
        </h3>
        
        {group.short_description && (
          <p className="text-sm text-stone-600 line-clamp-2 mb-4">
            {group.short_description}
          </p>
        )}
        
        <div className="flex items-center text-amber-700 text-sm font-medium group-hover:gap-2 transition-all">
          <span>View Collection</span>
          <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

function FeaturedSection({ groups }) {
  if (!groups || groups.length === 0) return null;
  
  return (
    <section className="mb-16">
      <h2 className="text-2xl font-bold text-stone-900 mb-6 border-b border-amber-200 pb-3">
        Featured Collections
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.filter(g => g.is_featured).slice(0, 6).map(group => (
          <CollectionGroupCard key={group.id} group={group} />
        ))}
      </div>
    </section>
  );
}

export default function CollectionGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    async function fetchGroups() {
      try {
        const response = await fetch(`${API_BASE}/api/collection-groups/public`);
        if (!response.ok) throw new Error('Failed to load collections');
        const data = await response.json();
        setGroups(data.groups || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchGroups();
  }, []);
  
  const featuredGroups = groups.filter(g => g.is_featured);
  const otherGroups = groups.filter(g => !g.is_featured);
  
  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-stone-900 mb-4">
            Archive Collections
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Explore our curated collections of artifacts, tickets, and memorabilia
            from the historic Aloha from Hawaii concert.
          </p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-700" />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-stone-500">{error}</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-16">
            <Layers className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500">No collections available yet.</p>
          </div>
        ) : (
          <>
            {/* Featured Collections */}
            <FeaturedSection groups={groups} />
            
            {/* All Collections */}
            {otherGroups.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold text-stone-900 mb-6 border-b border-stone-200 pb-3">
                  All Collections
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {otherGroups.map(group => (
                    <CollectionGroupCard key={group.id} group={group} />
                  ))}
                </div>
              </section>
            )}
            
            {/* Browse All Artifacts Link */}
            <div className="mt-16 text-center">
              <Link
                to="/collection"
                className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                <Package className="w-5 h-5" />
                Browse All Artifacts
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
